import React, { useEffect, useState } from 'react';
import {
  Mail,
  ShieldCheck,
  Users2,
  FileCode2,
  Send,
  RefreshCw,
  Save,
  RotateCcw,
  KeyRound,
  Eye,
  Code2,
  CheckCircle2,
  AlertCircle,
  Clock,
  Sliders,
  Lock,
  Edit3,
  Inbox,
} from 'lucide-react';
import {
  administrationService,
  EmailGovernanceSettings,
  EmailOutboxItem,
  EmailRolePolicy,
  EmailTemplateItem,
} from './administrationService';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

export interface EmailGovernanceWorkspaceProps {
  defaultSubTab?: 'role_matrix' | 'global_governance' | 'template_studio' | 'outbox_audit';
  initialSelectedRoleCode?: string;
  externalRoles?: Array<{ code: string; name: string }>;
}

const ALL_CANONICAL_ROLES = [
  'REPORTER',
  'SAFETY_OFFICER',
  'SAFETY_MANAGER',
  'INVESTIGATOR',
  'QUALITY_AUDITOR',
  'QUALITY_MANAGER',
  'COMPLIANCE_MANAGER',
  'DEPARTMENT_MANAGER',
  'OPERATIONS_CONTROL',
  'FLIGHT_OPS_MANAGER',
  'MAINTENANCE_MANAGER',
  'GROUND_OPS_MANAGER',
  'TRAINING_MANAGER',
  'DOCUMENT_CONTROLLER',
  'CHANGE_BOARD_MEMBER',
  'ACTION_OWNER',
  'ACCOUNTABLE_EXECUTIVE',
  'ERP_COORDINATOR',
  'IT_SECURITY_ADMIN',
  'SYSTEM_ADMIN',
];

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: '#475569',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid #cbd5e1',
  fontSize: '13px',
  color: '#1e293b',
  background: '#ffffff',
};

const cardStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: '10px',
  border: '1px solid #e2e8f0',
  padding: '20px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

const cardHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  marginBottom: '16px',
  borderBottom: '1px solid #f1f5f9',
  paddingBottom: '12px',
};

export const EmailGovernanceWorkspace: React.FC<EmailGovernanceWorkspaceProps> = ({
  defaultSubTab = 'global_governance',
  initialSelectedRoleCode,
  externalRoles = [],
}) => {
  const [subTab, setSubTab] = useState<'role_matrix' | 'global_governance' | 'template_studio' | 'outbox_audit'>(
    defaultSubTab,
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Data states
  const [governance, setGovernance] = useState<EmailGovernanceSettings>({
    organizationId: 'ORG-XYZ',
    enabled: true,
    deliveryMode: 'AUTO',
    provider: 'RESEND',
    senderName: 'SafetyCore Aviation SMS & QMS',
    fromAddress: 'notifications@xyz.luminixaviation.com',
    replyToAddress: 'safety-desk@xyz.luminixaviation.com',
    portalBaseUrl: 'https://xyz.luminixaviation.com',
    organizationName: 'XYZ Aviation',
    resendApiKeyConfigured: false,
    resendApiKeyMasked: '',
    complianceBccEmails: [],
    emergencyEscalationEmails: [],
    enforceJustCulturePiiQuarantine: true,
    redactConfidentialNarratives: true,
    blockAnonymousDirectOutreach: true,
    blockInternalSupportNotes: true,
    minimumRedactionLevel: 'RESTRICTED',
    quietHoursEnabled: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '06:00',
    bypassQuietHoursForCritical: true,
    dailyRateLimit: 5000,
    maxRetries: 3,
    retryBackoffSeconds: 60,
    morEscalationWarningHours: 24,
    actionOverdueReminderDays: 1,
    riskReviewReminderDays: 7,
    executiveDigestEnabled: true,
    executiveDigestCron: '0 7 * * *',
    customFooterDisclaimer:
      'ICAO Annex 19 / EU 376/2014 / EASA ORO.GEN.200 Just Culture & Data Protection Notice: Unauthorized forwarding or disclosure is strictly prohibited.',
  });
  const [rolePolicies, setRolePolicies] = useState<EmailRolePolicy[]>([]);
  const [templates, setTemplates] = useState<EmailTemplateItem[]>([]);
  const [outbox, setOutbox] = useState<EmailOutboxItem[]>([]);
  const { sortedItems: sortedOutbox, sortField: outboxSortField, sortDirection: outboxSortDir, toggleSort: toggleOutboxSort } = useTableSort(outbox);

  // Resend API Key Vault input
  const [apiKeyInput, setApiKeyInput] = useState('');

  // Role Matrix states
  const [selectedRoleCode, setSelectedRoleCode] = useState<string>(initialSelectedRoleCode || 'SAFETY_MANAGER');
  const [roleSearchFilter, setRoleSearchFilter] = useState('');
  const [roleTemplatePillarFilter, setRoleTemplatePillarFilter] = useState('ALL');
  const [fallbackEmailsText, setFallbackEmailsText] = useState('');
  const [autoCcEmailsText, setAutoCcEmailsText] = useState('');

  // Template Studio states
  const [selectedPillar, setSelectedPillar] = useState<string>('ALL');
  const [templateSearch, setTemplateSearch] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateItem | null>(null);
  const [previewMode, setPreviewMode] = useState<'normal' | 'confidential' | 'source' | 'editor'>('normal');
  const [testRecipientEmail, setTestRecipientEmail] = useState('');
  const [dispatchingTest, setDispatchingTest] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionDiagnostic, setConnectionDiagnostic] = useState<{
    success: boolean;
    apiKeyValid: boolean;
    apiKeySource: string;
    apiKeyMasked: string;
    isMockKey: boolean;
    senderDomain: string;
    domainVerified: boolean;
    domainStatus: string;
    accountDomains: Array<{ id: string; name: string; status: string }>;
    recommendation: string;
    error?: string;
    testEmailSent?: boolean;
    resendMessageId?: string;
    usedSender?: string;
    recipient?: string;
    sandboxFallbackUsed?: boolean;
    rootDomainFallbackUsed?: boolean;
  } | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  useEffect(() => {
    try {
      const uStr = localStorage.getItem('auth_user');
      if (uStr) {
        const u = JSON.parse(uStr);
        if (u.email) {
          setTestRecipientEmail((prev) => prev || u.email);
        }
      }
    } catch {}
  }, []);

  // Template Editor form state
  const [tplForm, setTplForm] = useState<{
    isActive: boolean;
    isSafetyCritical: boolean;
    defaultConfidentiality: string;
    subjectTemplate: string;
    headlineTemplate: string;
    introTemplate: string;
    bodySectionTitle: string;
    actionButtonLabel: string;
    portalTab: string;
    accentColor: string;
    badgeLabel: string;
    recipientRoles: string[];
    customToEmailsText: string;
    customCcEmailsText: string;
    customBccEmailsText: string;
    customHtmlOverride: string;
  } | null>(null);

  // Global governance text helpers for array fields
  const [complianceBccText, setComplianceBccText] = useState('');
  const [emergencyEscalationText, setEmergencyEscalationText] = useState('');

  const showMessage = (type: 'success' | 'error', text: string) => {
    setBanner({ type, text });
    setTimeout(() => setBanner(null), 5000);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [tplRes, outboxRes] = await Promise.all([
        administrationService.getEmailTemplates(),
        administrationService.getEmailOutbox(60),
      ]);

      const loadedTemplates = tplRes?.templates || [];
      setTemplates(loadedTemplates);
      setOutbox(outboxRes || []);

      const gov = tplRes?.governance || null;
      if (gov) {
        setGovernance(gov);
        setComplianceBccText((gov.complianceBccEmails || []).join(', '));
        setEmergencyEscalationText((gov.emergencyEscalationEmails || []).join(', '));
      }

      const mergedRolePolicies = [...(tplRes?.rolePolicies || [])];
      for (const extRole of externalRoles) {
        if (!mergedRolePolicies.some((r) => r.roleCode === extRole.code)) {
          mergedRolePolicies.push({
            organizationId: gov?.organizationId || 'default-org',
            roleCode: extRole.code,
            roleDisplayName: extRole.name || extRole.code,
            emailEnabled: true,
            deliveryPreference: 'IMMEDIATE',
            maxConfidentialityLevel: 'NORMAL',
            allowUnredactedNarrative: false,
            fallbackDistributionEmails: [],
            autoCcEmails: [],
            subscribedTemplateKeys: [],
            ccTemplateKeys: [],
            mutedTemplateKeys: [],
          });
        }
      }
      setRolePolicies(mergedRolePolicies);

      if (loadedTemplates.length > 0 && !selectedTemplate) {
        selectTemplateForStudio(loadedTemplates[0]);
      } else if (selectedTemplate) {
        const refreshed = loadedTemplates.find((t) => t.templateKey === selectedTemplate.templateKey);
        if (refreshed) selectTemplateForStudio(refreshed);
      }
    } catch (err: any) {
      showMessage('error', err?.message || 'Failed to load email configuration data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (initialSelectedRoleCode) {
      setSelectedRoleCode(initialSelectedRoleCode);
      setSubTab('role_matrix');
    }
  }, [initialSelectedRoleCode]);

  const currentRolePolicy =
    rolePolicies.find((r) => r.roleCode === selectedRoleCode) ||
    rolePolicies[0] || {
      organizationId: 'default-org',
      roleCode: selectedRoleCode,
      roleDisplayName: selectedRoleCode,
      emailEnabled: true,
      deliveryPreference: 'IMMEDIATE' as const,
      maxConfidentialityLevel: 'NORMAL' as const,
      allowUnredactedNarrative: false,
      fallbackDistributionEmails: [],
      autoCcEmails: [],
      subscribedTemplateKeys: [],
      ccTemplateKeys: [],
      mutedTemplateKeys: [],
    };

  useEffect(() => {
    setFallbackEmailsText((currentRolePolicy.fallbackDistributionEmails || []).join(', '));
    setAutoCcEmailsText((currentRolePolicy.autoCcEmails || []).join(', '));
  }, [currentRolePolicy.roleCode, rolePolicies]);

  const selectTemplateForStudio = (tpl: EmailTemplateItem) => {
    setSelectedTemplate(tpl);
    setTplForm({
      isActive: tpl.isActive !== false,
      isSafetyCritical: Boolean(tpl.isSafetyCritical),
      defaultConfidentiality: tpl.defaultConfidentiality || 'NORMAL',
      subjectTemplate: tpl.subjectTemplate || '',
      headlineTemplate: tpl.headlineTemplate || '',
      introTemplate: tpl.introTemplate || '',
      bodySectionTitle: tpl.bodySectionTitle || '',
      actionButtonLabel: tpl.actionButtonLabel || '',
      portalTab: tpl.portalTab || 'dashboard',
      accentColor: tpl.accentColor || '#0284c7',
      badgeLabel: tpl.badgeLabel || '',
      recipientRoles: Array.isArray(tpl.recipientRoles) ? [...tpl.recipientRoles] : [],
      customToEmailsText: (tpl.customToEmails || []).join(', '),
      customCcEmailsText: (tpl.customCcEmails || []).join(', '),
      customBccEmailsText: (tpl.customBccEmails || []).join(', '),
      customHtmlOverride: tpl.customHtmlOverride || '',
    });
  };

  const parseEmailCSV = (raw: string): string[] =>
    raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const handleSaveGovernance = async (e?: React.FormEvent, clearKey: boolean = false) => {
    if (e) e.preventDefault();
    if (!governance) return;
    setSaving(true);
    try {
      const updated = await administrationService.updateEmailGovernance({
        ...governance,
        complianceBccEmails: parseEmailCSV(complianceBccText),
        emergencyEscalationEmails: parseEmailCSV(emergencyEscalationText),
        resendApiKeyPlaintext: apiKeyInput.trim() ? apiKeyInput.trim() : undefined,
        clearResendApiKey: clearKey || undefined,
      });
      setGovernance(updated);
      setApiKeyInput('');
      setComplianceBccText((updated.complianceBccEmails || []).join(', '));
      setEmergencyEscalationText((updated.emergencyEscalationEmails || []).join(', '));
      showMessage(
        'success',
        clearKey
          ? 'Resend API key cleared from the encrypted vault and configuration updated.'
          : 'Email provider, security, and SLA configuration saved successfully.',
      );
      await loadAll();
    } catch (err: any) {
      showMessage('error', err?.message || 'Failed to save email configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRolePolicy = async (updatedPolicy?: Partial<EmailRolePolicy>) => {
    setSaving(true);
    try {
      const payload: Partial<EmailRolePolicy> = {
        ...currentRolePolicy,
        fallbackDistributionEmails: parseEmailCSV(fallbackEmailsText),
        autoCcEmails: parseEmailCSV(autoCcEmailsText),
        ...updatedPolicy,
      };
      const saved = await administrationService.updateEmailRolePolicy(currentRolePolicy.roleCode, payload);
      setRolePolicies((prev) => {
        const exists = prev.some((p) => p.roleCode === saved.roleCode);
        return exists ? prev.map((p) => (p.roleCode === saved.roleCode ? saved : p)) : [...prev, saved];
      });
      showMessage(
        'success',
        `Email routing and template subscriptions saved for role ${saved.roleDisplayName} (${saved.roleCode}).`,
      );
    } catch (err: any) {
      showMessage('error', err?.message || 'Failed to update role email policy.');
    } finally {
      setSaving(false);
    }
  };

  const toggleRoleTemplateSubscription = (
    templateKey: string,
    listField: 'subscribedTemplateKeys' | 'ccTemplateKeys' | 'mutedTemplateKeys',
  ) => {
    const currentList = currentRolePolicy[listField] || [];
    const exists = currentList.includes(templateKey);
    const nextList = exists ? currentList.filter((k) => k !== templateKey) : [...currentList, templateKey];

    setRolePolicies((prev) =>
      prev.map((p) => (p.roleCode === currentRolePolicy.roleCode ? { ...p, [listField]: nextList } : p)),
    );
  };

  const handleBulkRoleSubscription = (mode: 'ALL' | 'CRITICAL_ONLY' | 'NONE') => {
    let nextSubscribed: string[] = [];
    if (mode === 'ALL') {
      nextSubscribed = templates.map((t) => t.templateKey);
    } else if (mode === 'CRITICAL_ONLY') {
      nextSubscribed = templates.filter((t) => t.isSafetyCritical).map((t) => t.templateKey);
    }
    setRolePolicies((prev) =>
      prev.map((p) =>
        p.roleCode === currentRolePolicy.roleCode
          ? { ...p, subscribedTemplateKeys: nextSubscribed, mutedTemplateKeys: [] }
          : p,
      ),
    );
  };

  const handleSaveTemplateCustomization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplate || !tplForm) return;
    setSaving(true);
    try {
      const updated = await administrationService.updateEmailTemplate(selectedTemplate.templateKey, {
        isActive: tplForm.isActive,
        isSafetyCritical: tplForm.isSafetyCritical,
        defaultConfidentiality: tplForm.defaultConfidentiality,
        subjectTemplate: tplForm.subjectTemplate,
        headlineTemplate: tplForm.headlineTemplate,
        introTemplate: tplForm.introTemplate,
        bodySectionTitle: tplForm.bodySectionTitle,
        actionButtonLabel: tplForm.actionButtonLabel,
        portalTab: tplForm.portalTab,
        accentColor: tplForm.accentColor,
        badgeLabel: tplForm.badgeLabel,
        recipientRoles: tplForm.recipientRoles,
        customToEmails: parseEmailCSV(tplForm.customToEmailsText),
        customCcEmails: parseEmailCSV(tplForm.customCcEmailsText),
        customBccEmails: parseEmailCSV(tplForm.customBccEmailsText),
        customHtmlOverride: tplForm.customHtmlOverride,
      });
      setTemplates((prev) => prev.map((t) => (t.templateKey === updated.templateKey ? updated : t)));
      selectTemplateForStudio(updated);
      showMessage(
        'success',
        `Template ${updated.templateKey} (v${updated.version || 2}) saved and published successfully.`,
      );
    } catch (err: any) {
      showMessage('error', err?.message || 'Failed to save template configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetTemplate = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const reset = await administrationService.resetEmailTemplate(selectedTemplate.templateKey);
      setTemplates((prev) => prev.map((t) => (t.templateKey === reset.templateKey ? reset : t)));
      selectTemplateForStudio(reset);
      showMessage('success', `Template ${reset.templateKey} restored to default configuration.`);
    } catch (err: any) {
      showMessage('error', err?.message || 'Failed to reset template.');
    } finally {
      setSaving(false);
    }
  };

  const handleDispatchTestEmail = async () => {
    if (!selectedTemplate) return;
    setDispatchingTest(true);
    try {
      await administrationService.dispatchEmailNotification({
        templateKey: selectedTemplate.templateKey,
        recipientEmail: testRecipientEmail.trim() || 'safety-audit-test@aviation-sms.local',
        confidentialityLevel: previewMode === 'confidential' ? 'CONFIDENTIAL' : 'NORMAL',
        identityMode: previewMode === 'confidential' ? 'CONFIDENTIAL' : 'IDENTIFIED',
      });
      const updatedOutbox = await administrationService.getEmailOutbox(60);
      setOutbox(updatedOutbox);
      showMessage('success', `Test notification for ${selectedTemplate.templateKey} dispatched via Resend Outbox.`);
    } catch (err: any) {
      showMessage('error', err?.message || 'Failed to dispatch test email.');
    } finally {
      setDispatchingTest(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionDiagnostic(null);
    try {
      let activeUserEmail = testRecipientEmail.trim();
      if (!activeUserEmail) {
        try {
          const uStr = localStorage.getItem('auth_user');
          if (uStr) {
            const u = JSON.parse(uStr);
            activeUserEmail = u.email || '';
          }
        } catch {}
      }
      const targetRecipient = activeUserEmail || 'mustafakucukakarsu@gmail.com';

      const res = await administrationService.testResendConnection({
        apiKey: apiKeyInput.trim() || undefined,
        fromAddress: governance.fromAddress,
        recipientEmail: targetRecipient,
      });
      setConnectionDiagnostic(res);
      if (res.testEmailSent) {
        showMessage('success', `✓ Resend test e-postası '${res.recipient || targetRecipient}' adresine başarıyla iletildi (ID: ${res.resendMessageId || 'OK'})!`);
        try {
          const updatedOutbox = await administrationService.getEmailOutbox(60);
          setOutbox(updatedOutbox);
        } catch {}
      } else if (res.apiKeyValid) {
        showMessage('error', res.recommendation);
      } else {
        showMessage('error', res.error || 'Resend API bağlantısı başarısız.');
      }
    } catch (err: any) {
      showMessage('error', err?.message || 'Bağlantı testi sırasında hata oluştu.');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleRetryOutbox = async (id: string) => {
    setRetryingId(id);
    try {
      const updated = await administrationService.retryOutboxItem(id);
      setOutbox((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      if (updated.status === 'SENT') {
        showMessage('success', `✓ E-posta başarıyla Resend üzerinden iletildi (ID: ${updated.providerMessageId || id})`);
      } else {
        showMessage('error', `Yeniden deneme durumu: ${updated.status}. ${updated.errorMessage || ''}`);
      }
    } catch (err: any) {
      showMessage('error', err?.message || 'Gönderim yeniden denenirken hata oluştu.');
    } finally {
      setRetryingId(null);
    }
  };

  const handleRunSlaSweep = async () => {
    setDispatchingTest(true);
    try {
      const summary = await administrationService.runEmailSlaSweep();
      const updatedOutbox = await administrationService.getEmailOutbox(60);
      setOutbox(updatedOutbox);
      showMessage(
        'success',
        `SLA & Escalation Sweep completed: ${summary?.dispatchedCount || 0} notifications processed.`,
      );
    } catch (err: any) {
      showMessage('error', err?.message || 'Failed to run SLA sweep.');
    } finally {
      setDispatchingTest(false);
    }
  };

  const pillars = ['ALL', ...Array.from(new Set(templates.map((t) => t.pillarName)))];
  const filteredTemplates = templates.filter((t) => {
    const matchesPillar = selectedPillar === 'ALL' || t.pillarName === selectedPillar;
    const q = templateSearch.trim().toLowerCase();
    const matchesQuery =
      !q ||
      t.templateKey.toLowerCase().includes(q) ||
      t.eventTrigger.toLowerCase().includes(q) ||
      t.subjectTemplate.toLowerCase().includes(q) ||
      t.moduleDomain.toLowerCase().includes(q);
    return matchesPillar && matchesQuery;
  });

  const roleFilteredTemplates = templates.filter(
    (t) => roleTemplatePillarFilter === 'ALL' || t.pillarName === roleTemplatePillarFilter,
  );

  return (
    <div>
      {/* Status Banner */}
      {banner && (
        <div
          style={{
            background: banner.type === 'success' ? '#ecfdf5' : '#fef2f2',
            border: `1px solid ${banner.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
            color: banner.type === 'success' ? '#065f46' : '#991b1b',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '13px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {banner.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            <span>{banner.text}</span>
          </div>
          <button
            type="button"
            onClick={() => setBanner(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Top Status Strip (Matches AdministrationPage style) */}
      <div
        style={{
          ...cardStyle,
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
              Email Delivery Service
            </div>
            <div
              style={{
                fontSize: '14px',
                fontWeight: 700,
                color: '#0f172a',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '2px',
              }}
            >
              <Mail size={15} color="#2563eb" />
              {governance?.provider || 'RESEND'} — {governance?.senderName} &lt;{governance?.fromAddress}&gt;
            </div>
          </div>

          <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: '16px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
              Delivery Mode &amp; Vault
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
              <span className={`badge-pill ${governance?.enabled ? 'badge-success' : 'badge-danger'}`}>
                {governance?.enabled ? governance.deliveryMode : 'DISABLED'}
              </span>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>
                {governance?.resendApiKeyConfigured
                  ? `API Key: ${governance.resendApiKeyMasked}`
                  : 'Simulated Outbox (No API Key)'}
              </span>
            </div>
          </div>

          <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: '16px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
              Information Security (EU 376/2014)
            </div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#059669', marginTop: '2px' }}>
              {governance?.enforceJustCulturePiiQuarantine
                ? 'Just Culture PII Redaction Active'
                : 'Standard Redaction'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleRunSlaSweep}
            disabled={dispatchingTest}
            className="btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', fontSize: '12px' }}
          >
            <Clock size={14} /> Run SLA &amp; Escalation Sweep
          </button>
          <button
            type="button"
            onClick={loadAll}
            className="btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', fontSize: '12px' }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Standard Platform Navigation Tabs (.nav-tabs & .tab-btn) */}
      <div className="nav-tabs">
        <button
          type="button"
          onClick={() => setSubTab('global_governance')}
          className={`tab-btn ${subTab === 'global_governance' ? 'active' : ''}`}
        >
          <Sliders size={15} /> Email Configuration &amp; Provider
        </button>
        <button
          type="button"
          onClick={() => setSubTab('role_matrix')}
          className={`tab-btn ${subTab === 'role_matrix' ? 'active' : ''}`}
        >
          <Users2 size={15} /> Role Email Routing ({rolePolicies.length})
        </button>
        <button
          type="button"
          onClick={() => setSubTab('template_studio')}
          className={`tab-btn ${subTab === 'template_studio' ? 'active' : ''}`}
        >
          <FileCode2 size={15} /> Email Templates ({templates.length})
        </button>
        <button
          type="button"
          onClick={() => setSubTab('outbox_audit')}
          className={`tab-btn ${subTab === 'outbox_audit' ? 'active' : ''}`}
        >
          <Inbox size={15} /> Outbox &amp; Delivery Log ({outbox.length})
        </button>
      </div>

      {/* =====================================================================
          TAB 1: EMAIL CONFIGURATION & PROVIDER SETTINGS
         ===================================================================== */}
      {subTab === 'global_governance' && governance && (
        <form onSubmit={(e) => handleSaveGovernance(e, false)}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))',
              gap: '20px',
              marginBottom: '24px',
            }}
          >
            {/* Delivery Mode Warning if SIMULATED_OUTBOX */}
            {governance.deliveryMode === 'SIMULATED_OUTBOX' && (
              <div
                style={{
                  gridColumn: '1 / -1',
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <AlertCircle size={22} color="#d97706" style={{ flexShrink: 0 }} />
                <div style={{ fontSize: '13px', color: '#92400e', lineHeight: 1.5 }}>
                  <strong>Dikkat: Teslimat Modu SIMULATED_OUTBOX (Yalnızca Simülasyon) Olarak Ayarlı!</strong>
                  <br />
                  Sistemdeki tüm e-postalar veritabanı giden kutusunda (Outbox) güvenli bir şekilde simüle edilir ve harici Resend sunucusuna gönderilmez. Canlı e-posta gönderimi için Teslimat Modunu <strong>AUTO</strong> veya <strong>LIVE_RESEND</strong> yapınız.
                </div>
              </div>
            )}

            {/* Mock / Placeholder Key Warning */}
            {governance.isMockKey && (
              <div
                style={{
                  gridColumn: '1 / -1',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <AlertCircle size={22} color="#dc2626" style={{ flexShrink: 0 }} />
                <div style={{ fontSize: '13px', color: '#991b1b', lineHeight: 1.5 }}>
                  <strong>Uyarı: Kayıtlı Resend API Anahtarı Bir Test/Mock Anahtarıdır!</strong>
                  <br />
                  Canlı e-posta gönderimi yapılabilmesi için lütfen{' '}
                  <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', fontWeight: 700, color: '#991b1b' }}>
                    resend.com/api-keys
                  </a>{' '}
                  adresinden oluşturacağınız gerçek API anahtarınızı (<code>re_...</code>) aşağıdaki kasaya girip kaydediniz.
                </div>
              </div>
            )}

            {/* Card 1: Resend Provider & Sender Identity */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Mail size={18} color="#2563eb" />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                    Resend Provider &amp; Sender Identity
                  </h3>
                </div>
                <span className={`badge-pill ${governance.enabled ? 'badge-success' : 'badge-danger'}`}>
                  {governance.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>

              <div
                style={{
                  padding: '12px 14px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px',
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                    Enable Organization Email Notifications
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>
                    Master switch to enable or suspend outbound email delivery across all modules.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={governance.enabled}
                  onChange={(e) => setGovernance({ ...governance, enabled: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                <div>
                  <label style={labelStyle}>Delivery Mode</label>
                  <select
                    value={governance.deliveryMode}
                    onChange={(e) =>
                      setGovernance({
                        ...governance,
                        deliveryMode: e.target.value as EmailGovernanceSettings['deliveryMode'],
                      })
                    }
                    style={inputStyle}
                  >
                    <option value="AUTO">AUTO (Live Resend if API Key Set, else Outbox)</option>
                    <option value="LIVE_RESEND">LIVE_RESEND (Strict Live Resend Delivery)</option>
                    <option value="SIMULATED_OUTBOX">SIMULATED_OUTBOX (Outbox Capture Only)</option>
                    <option value="CRITICAL_ONLY">CRITICAL_ONLY (Safety-Critical Alerts Only)</option>
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Sender Display Name</label>
                  <input
                    type="text"
                    value={governance.senderName}
                    onChange={(e) => setGovernance({ ...governance, senderName: e.target.value })}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>From Email Address</label>
                  <input
                    type="email"
                    value={governance.fromAddress}
                    onChange={(e) => setGovernance({ ...governance, fromAddress: e.target.value })}
                    style={inputStyle}
                  />
                  <div style={{ marginTop: '5px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Hızlı Seçim:</span>
                    <button
                      type="button"
                      onClick={() => setGovernance({ ...governance, fromAddress: 'notifications@luminixaviation.com' })}
                      style={{ fontSize: '11px', color: '#16a34a', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontWeight: 600 }}
                      title="Kurumsal ana etki alanı (Resend'de 'luminixaviation.com' DNS DKIM/SPF doğrulaması ile tam uyumlu)"
                    >
                      Ana Domain (notifications@luminixaviation.com)
                    </button>
                    <span style={{ fontSize: '11px', color: '#cbd5e1' }}>|</span>
                    <button
                      type="button"
                      onClick={() => setGovernance({ ...governance, fromAddress: 'notifications@xyz.luminixaviation.com' })}
                      style={{ fontSize: '11px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                      title="Alt etki alanı (Resend'de 'xyz.luminixaviation.com' olarak ayrı DNS DKIM/SPF kaydı gerekir)"
                    >
                      Alt Domain (notifications@xyz.luminixaviation.com)
                    </button>
                    <span style={{ fontSize: '11px', color: '#cbd5e1' }}>|</span>
                    <button
                      type="button"
                      onClick={() => setGovernance({ ...governance, fromAddress: 'onboarding@resend.dev' })}
                      style={{ fontSize: '11px', color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                      title="Resend varsayılan sandbox etki alanı (DNS gerektirmez, sadece kayıtlı hesap e-postasına gönderir)"
                    >
                      Sandbox (onboarding@resend.dev)
                    </button>
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Reply-To Email Address</label>
                  <input
                    type="email"
                    value={governance.replyToAddress}
                    onChange={(e) => setGovernance({ ...governance, replyToAddress: e.target.value })}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Portal Base URL (Deep-Link Target)</label>
                <input
                  type="url"
                  value={governance.portalBaseUrl}
                  onChange={(e) => setGovernance({ ...governance, portalBaseUrl: e.target.value })}
                  style={inputStyle}
                />
              </div>

              {/* AES-256-GCM Encrypted Resend API Key Vault */}
              <div
                style={{
                  padding: '14px',
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '8px',
                  marginBottom: '14px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 700,
                      color: '#1e3a8a',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <KeyRound size={14} /> Resend API Key Vault (AES-256-GCM Encrypted)
                  </span>
                  <span className={`badge-pill ${governance.isMockKey ? 'badge-warning' : governance.resendApiKeyConfigured ? 'badge-info' : 'badge-neutral'}`} style={{ fontSize: '11px' }}>
                    {governance.isMockKey
                      ? 'Mock Key Configured (Test Mode)'
                      : governance.resendApiKeyConfigured
                      ? `Configured: ${governance.resendApiKeyMasked}`
                      : 'Not Configured'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    type="password"
                    value={apiKeyInput}
                    onChange={(e) => setApiKeyInput(e.target.value)}
                    placeholder="Resend API Anahtarı (re_...) — Mevcut şifreli anahtarı korumak için boş bırakınız"
                    style={{ ...inputStyle, flex: 2, minWidth: '260px' }}
                  />
                  <input
                    type="email"
                    value={testRecipientEmail}
                    onChange={(e) => setTestRecipientEmail(e.target.value)}
                    placeholder="Hedef test e-postası (örn. mustafakucukakarsu@gmail.com)"
                    style={{ ...inputStyle, flex: 1, minWidth: '220px' }}
                    title="Bağlantı testi yapıldığında doğrulama e-postasının gönderileceği hedef adres"
                  />
                  <button
                    type="button"
                    onClick={handleTestConnection}
                    disabled={testingConnection}
                    className="btn-secondary"
                    style={{
                      padding: '7px 14px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontWeight: 600,
                      background: '#eff6ff',
                      borderColor: '#3b82f6',
                      color: '#1d4ed8',
                    }}
                  >
                    <RefreshCw size={13} className={testingConnection ? 'spin' : ''} />
                    {testingConnection ? 'Doğrulanıyor & Gönderiliyor...' : 'Test Connection & Domain'}
                  </button>
                  {governance.resendApiKeyConfigured && (
                    <button
                      type="button"
                      onClick={() => handleSaveGovernance(undefined, true)}
                      className="btn-secondary"
                      style={{ color: '#dc2626', borderColor: '#fca5a5', padding: '6px 12px', fontSize: '12px' }}
                    >
                      Clear Key
                    </button>
                  )}
                </div>

                {/* Connection Diagnostic Result Panel */}
                {connectionDiagnostic && (
                  <div
                    style={{
                      marginTop: '12px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      background: connectionDiagnostic.testEmailSent
                        ? connectionDiagnostic.sandboxFallbackUsed
                          ? '#fefce8'
                          : '#f0fdf4'
                        : '#fef2f2',
                      border: `1px solid ${
                        connectionDiagnostic.testEmailSent
                          ? connectionDiagnostic.sandboxFallbackUsed
                            ? '#fde047'
                            : '#86efac'
                          : '#fca5a5'
                      }`,
                      fontSize: '12px',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        marginBottom: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: connectionDiagnostic.testEmailSent
                          ? connectionDiagnostic.sandboxFallbackUsed
                            ? '#854d0e'
                            : '#166534'
                          : '#991b1b',
                      }}
                    >
                      {connectionDiagnostic.testEmailSent ? (
                        <>
                          <span>✓ Resend Test E-postası Başarıyla Gönderildi!</span>
                          {connectionDiagnostic.sandboxFallbackUsed && (
                            <span className="badge-pill badge-warning" style={{ fontSize: '10px' }}>
                              Sandbox (onboarding@resend.dev)
                            </span>
                          )}
                          {connectionDiagnostic.rootDomainFallbackUsed && (
                            <>
                              <span className="badge-pill badge-success" style={{ fontSize: '10px', background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}>
                                ✓ Ana Domain Doğrulandı ({connectionDiagnostic.usedSender})
                              </span>
                              <button
                                type="button"
                                onClick={() => setGovernance(prev => ({ ...prev, fromAddress: connectionDiagnostic.usedSender || 'notifications@luminixaviation.com' }))}
                                style={{
                                  fontSize: '11px',
                                  color: '#166534',
                                  background: '#ffffff',
                                  border: '1px solid #86efac',
                                  borderRadius: '4px',
                                  padding: '2px 8px',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                }}
                                title="Gönderen adresini doğrulanmış kurumsal ana adrese ayarla"
                              >
                                Gönderen Adresi Yap
                              </button>
                            </>
                          )}
                          {connectionDiagnostic.domainVerified && !connectionDiagnostic.sandboxFallbackUsed && !connectionDiagnostic.rootDomainFallbackUsed && (
                            <span className="badge-pill badge-success" style={{ fontSize: '10px' }}>
                              Alan Adı Doğrulandı
                            </span>
                          )}
                        </>
                      ) : connectionDiagnostic.apiKeyValid ? (
                        <span>⚠️ Resend API Anahtarı Geçerli Ancak Gönderim Başarısız Oldu</span>
                      ) : (
                        <span>❌ Resend API Anahtarı Geçersiz</span>
                      )}
                    </div>

                    {connectionDiagnostic.resendMessageId && (
                      <div
                        style={{
                          marginBottom: '6px',
                          color: '#1e3a8a',
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          background: '#ffffff',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        Resend Mesaj ID: <strong>{connectionDiagnostic.resendMessageId}</strong> &bull; Alıcı: <strong>{connectionDiagnostic.recipient}</strong> &bull; Gönderen: <strong>{connectionDiagnostic.usedSender}</strong>
                      </div>
                    )}

                    <div style={{ color: '#334155', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                      {connectionDiagnostic.recommendation}
                    </div>

                    {connectionDiagnostic.testEmailSent && (
                      <div style={{ marginTop: '8px', fontSize: '11px', color: '#475569' }}>
                        🚀 <strong>Resend Panosu:</strong> Canlı teslimat durumunu ve açılma loglarını incelemek için{' '}
                        <a
                          href="https://resend.com/emails"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 600 }}
                        >
                          resend.com/emails
                        </a>{' '}
                        adresini kontrol edebilirsiniz.
                      </div>
                    )}

                    {connectionDiagnostic.error && (
                      <div
                        style={{
                          marginTop: '6px',
                          color: '#dc2626',
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          background: '#ffffff',
                          padding: '6px 8px',
                          borderRadius: '4px',
                          border: '1px solid #fee2e2',
                        }}
                      >
                        Resend Yanıtı: {connectionDiagnostic.error}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: '#1e40af', marginTop: '6px' }}>
                  Encrypted at rest using AES-256-GCM. Plaintext keys are never exposed in API responses or logs.
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={labelStyle}>Compliance Archive BCC Emails</label>
                  <input
                    type="text"
                    value={complianceBccText}
                    onChange={(e) => setComplianceBccText(e.target.value)}
                    placeholder="compliance-archive@airline.com"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Emergency &amp; AOG Escalation Emails</label>
                  <input
                    type="text"
                    value={emergencyEscalationText}
                    onChange={(e) => setEmergencyEscalationText(e.target.value)}
                    placeholder="erc-duty@airline.com"
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>

            {/* Card 2: Information Security & Just Culture */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ShieldCheck size={18} color="#059669" />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                    Information Security &amp; Just Culture Protection
                  </h3>
                </div>
                <span className="badge-pill badge-info">EU 376/2014</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                {[
                  {
                    key: 'enforceJustCulturePiiQuarantine' as const,
                    title: 'Enforce Just Culture Reporter PII Quarantine',
                    desc: 'Automatically masks reporter name, email, phone, and license identifiers on confidential reports.',
                  },
                  {
                    key: 'redactConfidentialNarratives' as const,
                    title: 'Redact Confidential & Restricted Narratives',
                    desc: 'Replaces sensitive occurrence/investigation narratives with an authenticated portal link.',
                  },
                  {
                    key: 'blockAnonymousDirectOutreach' as const,
                    title: 'Block Direct Outreach to Anonymous Reporters',
                    desc: 'Prevents outbound emails to anonymous submissions to preserve zero-knowledge anonymity.',
                  },
                  {
                    key: 'blockInternalSupportNotes' as const,
                    title: 'Block Internal Support & Triage Notes',
                    desc: 'Ensures internal helpdesk notes are never transmitted in external email notifications.',
                  },
                ].map((item) => (
                  <div
                    key={item.key}
                    style={{
                      padding: '10px 14px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{item.title}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{item.desc}</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={Boolean(governance[item.key])}
                      onChange={(e) => setGovernance({ ...governance, [item.key]: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                    />
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Minimum Default Redaction Level</label>
                <select
                  value={governance.minimumRedactionLevel}
                  onChange={(e) =>
                    setGovernance({
                      ...governance,
                      minimumRedactionLevel: e.target.value as EmailGovernanceSettings['minimumRedactionLevel'],
                    })
                  }
                  style={inputStyle}
                >
                  <option value="NORMAL">NORMAL — Use Template Default Classification</option>
                  <option value="RESTRICTED">RESTRICTED — Treat All Outbound Emails as Restricted</option>
                  <option value="CONFIDENTIAL">CONFIDENTIAL — Enforce Full Narrative &amp; PII Redaction</option>
                </select>
              </div>

              <div>
                <label style={labelStyle}>Email Footer Legal &amp; Compliance Disclaimer</label>
                <textarea
                  rows={3}
                  value={governance.customFooterDisclaimer}
                  onChange={(e) => setGovernance({ ...governance, customFooterDisclaimer: e.target.value })}
                  style={{ ...inputStyle, fontFamily: 'inherit' }}
                />
              </div>
            </div>

            {/* Card 3: Rate Limiting, Retries & Quiet Hours */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Clock size={18} color="#2563eb" />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                    Delivery Limits, Retry Policy &amp; Quiet Hours
                  </h3>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={labelStyle}>Max Retries</label>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    value={governance.maxRetries}
                    onChange={(e) => setGovernance({ ...governance, maxRetries: Number(e.target.value) })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Retry Backoff (sec)</label>
                  <input
                    type="number"
                    min={5}
                    max={3600}
                    value={governance.retryBackoffSeconds}
                    onChange={(e) => setGovernance({ ...governance, retryBackoffSeconds: Number(e.target.value) })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Daily Rate Limit</label>
                  <input
                    type="number"
                    min={10}
                    max={500000}
                    value={governance.dailyRateLimit}
                    onChange={(e) => setGovernance({ ...governance, dailyRateLimit: Number(e.target.value) })}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div
                style={{
                  padding: '10px 14px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '14px',
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>Enable Quiet Hours</div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>
                    Hold non-critical notifications during off-duty hours.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={governance.quietHoursEnabled}
                  onChange={(e) => setGovernance({ ...governance, quietHoursEnabled: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <label style={labelStyle}>Quiet Hours Start</label>
                  <input
                    type="time"
                    value={governance.quietHoursStart}
                    onChange={(e) => setGovernance({ ...governance, quietHoursStart: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Quiet Hours End</label>
                  <input
                    type="time"
                    value={governance.quietHoursEnd}
                    onChange={(e) => setGovernance({ ...governance, quietHoursEnd: e.target.value })}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div
                style={{
                  padding: '10px 14px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#991b1b' }}>
                    Bypass Quiet Hours for Safety-Critical Alerts
                  </div>
                  <div style={{ fontSize: '11px', color: '#7f1d1d' }}>
                    MOR, AOG, ERP, and Extreme Risk alerts are always delivered immediately.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={governance.bypassQuietHoursForCritical}
                  onChange={(e) => setGovernance({ ...governance, bypassQuietHoursForCritical: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
              </div>
            </div>

            {/* Card 4: Automated SLA & Escalation Timers */}
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Sliders size={18} color="#059669" />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                    Automated SLA &amp; Escalation Thresholds
                  </h3>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={labelStyle}>MOR SLA Warning (Hours)</label>
                  <input
                    type="number"
                    min={1}
                    max={168}
                    value={governance.morEscalationWarningHours}
                    onChange={(e) =>
                      setGovernance({ ...governance, morEscalationWarningHours: Number(e.target.value) })
                    }
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>CAPA Reminder (Days)</label>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={governance.actionOverdueReminderDays}
                    onChange={(e) =>
                      setGovernance({ ...governance, actionOverdueReminderDays: Number(e.target.value) })
                    }
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Risk Review Alert (Days)</label>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={governance.riskReviewReminderDays}
                    onChange={(e) => setGovernance({ ...governance, riskReviewReminderDays: Number(e.target.value) })}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div
                style={{
                  padding: '10px 14px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '14px',
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                    Accountable Executive Weekly SMS Digest
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>
                    Send periodic SPI, open finding, and safety performance summary to executive leadership.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={governance.executiveDigestEnabled}
                  onChange={(e) => setGovernance({ ...governance, executiveDigestEnabled: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
              </div>

              <div>
                <label style={labelStyle}>Executive Digest Schedule (Cron Expression)</label>
                <input
                  type="text"
                  value={governance.executiveDigestCron}
                  onChange={(e) => setGovernance({ ...governance, executiveDigestCron: e.target.value })}
                  style={{ ...inputStyle, fontFamily: 'monospace' }}
                />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary"
              style={{ width: 'auto', padding: '10px 24px', borderRadius: '8px' }}
            >
              <Save size={16} /> {saving ? 'Saving...' : 'Save Email Configuration'}
            </button>
          </div>
        </form>
      )}

      {/* =====================================================================
          TAB 2: ROLE EMAIL ROUTING & SUBSCRIPTIONS
         ===================================================================== */}
      {subTab === 'role_matrix' && (
        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '20px', alignItems: 'start' }}>
          {/* Left: Role Selector List */}
          <div
            style={{
              ...cardStyle,
              padding: 0,
              overflow: 'hidden',
              maxHeight: '780px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ padding: '14px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>
                Aviation &amp; QMS Roles ({rolePolicies.length})
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>
                Select a role to configure its email delivery preferences and template subscriptions.
              </div>
              <input
                type="text"
                value={roleSearchFilter}
                onChange={(e) => setRoleSearchFilter(e.target.value)}
                placeholder="Search role code or name..."
                style={inputStyle}
              />
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {rolePolicies
                .filter(
                  (r) =>
                    !roleSearchFilter.trim() ||
                    r.roleCode.toLowerCase().includes(roleSearchFilter.toLowerCase()) ||
                    r.roleDisplayName.toLowerCase().includes(roleSearchFilter.toLowerCase()),
                )
                .map((rp) => {
                  const isSelected = rp.roleCode === currentRolePolicy.roleCode;
                  return (
                    <div
                      key={rp.roleCode}
                      onClick={() => setSelectedRoleCode(rp.roleCode)}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #f1f5f9',
                        cursor: 'pointer',
                        background: isSelected ? '#eff6ff' : '#ffffff',
                        borderLeft: isSelected ? '4px solid #2563eb' : '4px solid transparent',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{rp.roleDisplayName}</div>
                        <div style={{ fontSize: '11px', fontFamily: 'monospace', color: '#64748b' }}>{rp.roleCode}</div>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                          <span
                            className={`badge-pill ${
                              rp.emailEnabled && rp.deliveryPreference !== 'DISABLED' ? 'badge-success' : 'badge-danger'
                            }`}
                            style={{ fontSize: '10px', padding: '1px 7px' }}
                          >
                            {rp.emailEnabled ? rp.deliveryPreference : 'DISABLED'}
                          </span>
                          <span className="badge-pill badge-neutral" style={{ fontSize: '10px', padding: '1px 7px' }}>
                            {rp.maxConfidentialityLevel}
                          </span>
                        </div>
                      </div>
                      <span className="badge-pill badge-info" style={{ fontSize: '11px' }}>
                        {(rp.subscribedTemplateKeys || []).length}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Right: Selected Role Policy Configuration */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, fontFamily: 'monospace', color: '#2563eb' }}>
                  ROLE EMAIL POLICY • {currentRolePolicy.roleCode}
                </div>
                <h3 style={{ margin: '2px 0 0 0', fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                  {currentRolePolicy.roleDisplayName}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => handleSaveRolePolicy()}
                disabled={saving}
                className="btn-primary"
                style={{ width: 'auto', padding: '8px 18px', borderRadius: '8px', fontSize: '13px' }}
              >
                <Save size={15} /> {saving ? 'Saving...' : 'Save Role Email Policy'}
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
              <div
                style={{
                  padding: '12px 14px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>Enable Email for This Role</div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>
                    Allow users assigned to this role to receive system email notifications.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={currentRolePolicy.emailEnabled}
                  onChange={(e) =>
                    setRolePolicies((prev) =>
                      prev.map((p) =>
                        p.roleCode === currentRolePolicy.roleCode ? { ...p, emailEnabled: e.target.checked } : p,
                      ),
                    )
                  }
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
              </div>

              <div
                style={{
                  padding: '12px 14px',
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>
                    Allow Unredacted Narrative Clearance
                  </div>
                  <div style={{ fontSize: '11px', color: '#b45309' }}>
                    Recommended only for authorized Safety Manager / Investigator roles.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={currentRolePolicy.allowUnredactedNarrative}
                  onChange={(e) =>
                    setRolePolicies((prev) =>
                      prev.map((p) =>
                        p.roleCode === currentRolePolicy.roleCode
                          ? { ...p, allowUnredactedNarrative: e.target.checked }
                          : p,
                      ),
                    )
                  }
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
              </div>

              <div>
                <label style={labelStyle}>Delivery Preference</label>
                <select
                  value={currentRolePolicy.deliveryPreference}
                  onChange={(e) =>
                    setRolePolicies((prev) =>
                      prev.map((p) =>
                        p.roleCode === currentRolePolicy.roleCode
                          ? { ...p, deliveryPreference: e.target.value as EmailRolePolicy['deliveryPreference'] }
                          : p,
                      ),
                    )
                  }
                  style={inputStyle}
                >
                  <option value="IMMEDIATE">IMMEDIATE — Real-Time Delivery</option>
                  <option value="CRITICAL_ONLY">CRITICAL_ONLY — Safety-Critical Alerts Only</option>
                  <option value="DAILY_DIGEST">DAILY_DIGEST — Consolidated Daily Digest</option>
                  <option value="DISABLED">DISABLED — Suspend All Notifications for Role</option>
                </select>
              </div>

              <div>
                <label style={labelStyle}>Maximum Confidentiality Clearance</label>
                <select
                  value={currentRolePolicy.maxConfidentialityLevel}
                  onChange={(e) =>
                    setRolePolicies((prev) =>
                      prev.map((p) =>
                        p.roleCode === currentRolePolicy.roleCode
                          ? {
                              ...p,
                              maxConfidentialityLevel: e.target.value as EmailRolePolicy['maxConfidentialityLevel'],
                            }
                          : p,
                      ),
                    )
                  }
                  style={inputStyle}
                >
                  <option value="NORMAL">NORMAL — Standard Operational Notifications</option>
                  <option value="RESTRICTED">RESTRICTED — Restricted Operational Clearance</option>
                  <option value="CONFIDENTIAL">CONFIDENTIAL — Full Just Culture Clearance</option>
                </select>
              </div>

              <div>
                <label style={labelStyle}>Fallback Distribution Emails (Comma-separated)</label>
                <input
                  type="text"
                  value={fallbackEmailsText}
                  onChange={(e) => setFallbackEmailsText(e.target.value)}
                  placeholder="safety-desk@airline.com, duty-manager@airline.com"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Automatic CC Emails for This Role (Comma-separated)</label>
                <input
                  type="text"
                  value={autoCcEmailsText}
                  onChange={(e) => setAutoCcEmailsText(e.target.value)}
                  placeholder="archive@airline.com, deputy-sms@airline.com"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Template Subscription Matrix Table */}
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '10px',
                  marginBottom: '12px',
                }}
              >
                <div>
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                    Subscribed Email Templates ({(currentRolePolicy.subscribedTemplateKeys || []).length} of{' '}
                    {templates.length})
                  </h4>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    Configure TO (Primary Recipient), CC (Copy), or Mute rules per workflow event.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select
                    value={roleTemplatePillarFilter}
                    onChange={(e) => setRoleTemplatePillarFilter(e.target.value)}
                    style={{ ...inputStyle, width: 'auto', padding: '6px 10px', fontSize: '12px' }}
                  >
                    {pillars.map((p) => (
                      <option key={p} value={p}>
                        {p === 'ALL' ? 'All Pillars (54)' : p}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => handleBulkRoleSubscription('ALL')}
                    className="btn-secondary"
                    style={{ padding: '5px 10px', fontSize: '11px' }}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkRoleSubscription('CRITICAL_ONLY')}
                    className="btn-secondary"
                    style={{ padding: '5px 10px', fontSize: '11px' }}
                  >
                    Critical Only
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkRoleSubscription('NONE')}
                    className="btn-secondary"
                    style={{ padding: '5px 10px', fontSize: '11px' }}
                  >
                    Clear All
                  </button>
                </div>
              </div>

              <div className="table-container" style={{ maxHeight: '420px', overflowY: 'auto', marginBottom: 0 }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Template Key &amp; Trigger</th>
                      <th>Pillar</th>
                      <th style={{ textAlign: 'center' }}>TO (Primary)</th>
                      <th style={{ textAlign: 'center' }}>CC (Copy)</th>
                      <th style={{ textAlign: 'center' }}>Mute</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roleFilteredTemplates.map((tpl) => {
                      const isTo = (currentRolePolicy.subscribedTemplateKeys || []).includes(tpl.templateKey);
                      const isCc = (currentRolePolicy.ccTemplateKeys || []).includes(tpl.templateKey);
                      const isMuted = (currentRolePolicy.mutedTemplateKeys || []).includes(tpl.templateKey);

                      return (
                        <tr key={tpl.templateKey}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: 700, color: '#2563eb' }}>
                                {tpl.templateKey}
                              </span>
                              {tpl.isSafetyCritical && (
                                <span className="badge-pill badge-danger" style={{ fontSize: '10px', padding: '1px 6px' }}>
                                  CRITICAL
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '12px', color: '#334155', marginTop: '2px' }}>{tpl.eventTrigger}</div>
                          </td>
                          <td style={{ fontSize: '12px', color: '#64748b' }}>{tpl.pillarName}</td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isTo}
                              onChange={() => toggleRoleTemplateSubscription(tpl.templateKey, 'subscribedTemplateKeys')}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isCc}
                              onChange={() => toggleRoleTemplateSubscription(tpl.templateKey, 'ccTemplateKeys')}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isMuted}
                              onChange={() => toggleRoleTemplateSubscription(tpl.templateKey, 'mutedTemplateKeys')}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================================
          TAB 3: EMAIL TEMPLATES & CUSTOMIZATION STUDIO
         ===================================================================== */}
      {subTab === 'template_studio' && (
        <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: '20px', alignItems: 'start' }}>
          {/* Left: 54 Template Catalog */}
          <div
            style={{
              ...cardStyle,
              padding: 0,
              overflow: 'hidden',
              maxHeight: '780px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ padding: '14px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>
                Aviation SMS/QMS Email Catalog ({filteredTemplates.length} / {templates.length})
              </div>
              <input
                type="text"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                placeholder="Search template key, trigger, or subject..."
                style={{ ...inputStyle, marginBottom: '8px' }}
              />
              <select
                value={selectedPillar}
                onChange={(e) => setSelectedPillar(e.target.value)}
                style={inputStyle}
              >
                {pillars.map((p) => (
                  <option key={p} value={p}>
                    {p === 'ALL' ? `All 7 Aviation Regulatory Pillars (${templates.length})` : p}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {filteredTemplates.map((tpl) => {
                const isSelected = selectedTemplate?.templateKey === tpl.templateKey;
                return (
                  <div
                    key={tpl.templateKey}
                    onClick={() => selectTemplateForStudio(tpl)}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #f1f5f9',
                      cursor: 'pointer',
                      background: isSelected ? '#eff6ff' : '#ffffff',
                      borderLeft: isSelected ? '4px solid #2563eb' : '4px solid transparent',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '4px',
                      }}
                    >
                      <span style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: 700, color: '#2563eb' }}>
                        {tpl.templateKey}
                      </span>
                      <span
                        className={`badge-pill ${tpl.isSafetyCritical ? 'badge-danger' : 'badge-neutral'}`}
                        style={{ fontSize: '10px', padding: '1px 6px' }}
                      >
                        v{tpl.version || 1}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', marginBottom: '2px' }}>
                      {tpl.eventTrigger}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      {tpl.moduleDomain} • {tpl.recipientRoles.join(', ')}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Live Preview + Template Customization Editor */}
          {selectedTemplate && tplForm && (
            <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
              {/* Header Bar */}
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #e2e8f0',
                  background: '#f8fafc',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px',
                }}
              >
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#2563eb', fontFamily: 'monospace' }}>
                    {selectedTemplate.pillarName} / {selectedTemplate.moduleDomain} / {selectedTemplate.regulationRef} (v
                    {selectedTemplate.version || 1})
                  </div>
                  <h3 style={{ margin: '2px 0 4px 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                    {selectedTemplate.eventTrigger}
                  </h3>
                  <div style={{ fontSize: '12px', color: '#475569' }}>
                    <strong>Subject:</strong>{' '}
                    {previewMode === 'confidential'
                      ? selectedTemplate.renderedConfidential.subject
                      : selectedTemplate.renderedNormal.subject}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('normal')}
                    className="btn-secondary"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      background: previewMode === 'normal' ? '#2563eb' : '#ffffff',
                      color: previewMode === 'normal' ? '#ffffff' : '#334155',
                    }}
                  >
                    <Eye size={14} /> Standard Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('confidential')}
                    className="btn-secondary"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      background: previewMode === 'confidential' ? '#7c3aed' : '#ffffff',
                      color: previewMode === 'confidential' ? '#ffffff' : '#334155',
                    }}
                  >
                    <Lock size={14} /> Redacted Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('editor')}
                    className="btn-secondary"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      background: previewMode === 'editor' ? '#059669' : '#ffffff',
                      color: previewMode === 'editor' ? '#ffffff' : '#059669',
                      borderColor: '#059669',
                    }}
                  >
                    <Edit3 size={14} /> Customize Template
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('source')}
                    className="btn-secondary"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      background: previewMode === 'source' ? '#0f172a' : '#ffffff',
                      color: previewMode === 'source' ? '#ffffff' : '#334155',
                    }}
                  >
                    <Code2 size={14} /> HTML5 Source
                  </button>
                </div>
              </div>

              {previewMode === 'editor' ? (
                <form onSubmit={handleSaveTemplateCustomization} style={{ padding: '20px' }}>
                  <div
                    style={{
                      padding: '12px 16px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ fontSize: '12px', color: '#334155' }}>
                      Customize subject, text blocks, recipient roles, static CC/BCC addresses, or full HTML5 markup.
                    </div>
                    <button
                      type="button"
                      onClick={handleResetTemplate}
                      disabled={saving}
                      className="btn-secondary"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', fontSize: '12px' }}
                    >
                      <RotateCcw size={13} /> Reset to Default
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                    <label
                      style={{
                        padding: '10px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      <span>Template Active</span>
                      <input
                        type="checkbox"
                        checked={tplForm.isActive}
                        onChange={(e) => setTplForm({ ...tplForm, isActive: e.target.checked })}
                      />
                    </label>

                    <label
                      style={{
                        padding: '10px 12px',
                        border: '1px solid #fecaca',
                        background: '#fef2f2',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#991b1b',
                      }}
                    >
                      <span>Safety-Critical Alert</span>
                      <input
                        type="checkbox"
                        checked={tplForm.isSafetyCritical}
                        onChange={(e) => setTplForm({ ...tplForm, isSafetyCritical: e.target.checked })}
                      />
                    </label>

                    <div>
                      <select
                        value={tplForm.defaultConfidentiality}
                        onChange={(e) => setTplForm({ ...tplForm, defaultConfidentiality: e.target.value })}
                        style={inputStyle}
                      >
                        <option value="NORMAL">Classification: NORMAL</option>
                        <option value="RESTRICTED">Classification: RESTRICTED</option>
                        <option value="CONFIDENTIAL">Classification: CONFIDENTIAL</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={labelStyle}>Subject Template (Supports {'{{variable}}'} placeholders)</label>
                      <input
                        type="text"
                        value={tplForm.subjectTemplate}
                        onChange={(e) => setTplForm({ ...tplForm, subjectTemplate: e.target.value })}
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Headline Banner Text</label>
                      <input
                        type="text"
                        value={tplForm.headlineTemplate}
                        onChange={(e) => setTplForm({ ...tplForm, headlineTemplate: e.target.value })}
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Body Section Title</label>
                      <input
                        type="text"
                        value={tplForm.bodySectionTitle}
                        onChange={(e) => setTplForm({ ...tplForm, bodySectionTitle: e.target.value })}
                        style={inputStyle}
                      />
                    </div>

                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={labelStyle}>Introductory Paragraph</label>
                      <textarea
                        rows={2}
                        value={tplForm.introTemplate}
                        onChange={(e) => setTplForm({ ...tplForm, introTemplate: e.target.value })}
                        style={{ ...inputStyle, fontFamily: 'inherit' }}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Action Button Label (CTA)</label>
                      <input
                        type="text"
                        value={tplForm.actionButtonLabel}
                        onChange={(e) => setTplForm({ ...tplForm, actionButtonLabel: e.target.value })}
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Portal Target Tab</label>
                      <input
                        type="text"
                        value={tplForm.portalTab}
                        onChange={(e) => setTplForm({ ...tplForm, portalTab: e.target.value })}
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Header Badge Label</label>
                      <input
                        type="text"
                        value={tplForm.badgeLabel}
                        onChange={(e) => setTplForm({ ...tplForm, badgeLabel: e.target.value })}
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Brand Accent Color</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="color"
                          value={tplForm.accentColor}
                          onChange={(e) => setTplForm({ ...tplForm, accentColor: e.target.value })}
                          style={{ width: '44px', height: '36px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                        />
                        <input
                          type="text"
                          value={tplForm.accentColor}
                          onChange={(e) => setTplForm({ ...tplForm, accentColor: e.target.value })}
                          style={{ ...inputStyle, fontFamily: 'monospace' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Recipient Roles */}
                  <div style={{ marginBottom: '14px' }}>
                    <label style={labelStyle}>Default Recipient Roles</label>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        padding: '10px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                      }}
                    >
                      {ALL_CANONICAL_ROLES.map((role) => {
                        const active = tplForm.recipientRoles.includes(role);
                        return (
                          <button
                            key={role}
                            type="button"
                            onClick={() => {
                              const next = active
                                ? tplForm.recipientRoles.filter((r) => r !== role)
                                : [...tplForm.recipientRoles, role];
                              setTplForm({ ...tplForm, recipientRoles: next });
                            }}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontFamily: 'monospace',
                              fontWeight: 600,
                              cursor: 'pointer',
                              border: active ? '1px solid #2563eb' : '1px solid #cbd5e1',
                              background: active ? '#2563eb' : '#ffffff',
                              color: active ? '#ffffff' : '#475569',
                            }}
                          >
                            {active ? '✓ ' : '+ '}
                            {role}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                    <div>
                      <label style={labelStyle}>Additional Static TO Emails</label>
                      <input
                        type="text"
                        value={tplForm.customToEmailsText}
                        onChange={(e) => setTplForm({ ...tplForm, customToEmailsText: e.target.value })}
                        placeholder="to@airline.com"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Additional Static CC Emails</label>
                      <input
                        type="text"
                        value={tplForm.customCcEmailsText}
                        onChange={(e) => setTplForm({ ...tplForm, customCcEmailsText: e.target.value })}
                        placeholder="cc@airline.com"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Additional Static BCC Emails</label>
                      <input
                        type="text"
                        value={tplForm.customBccEmailsText}
                        onChange={(e) => setTplForm({ ...tplForm, customBccEmailsText: e.target.value })}
                        placeholder="bcc@airline.com"
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>
                      Custom Full HTML5 Override (Optional — leave blank to use standard responsive template)
                    </label>
                    <textarea
                      rows={5}
                      value={tplForm.customHtmlOverride}
                      onChange={(e) => setTplForm({ ...tplForm, customHtmlOverride: e.target.value })}
                      placeholder="Paste custom HTML5 markup with {{variable}} placeholders if required..."
                      style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '12px' }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={() => setPreviewMode('normal')}
                      className="btn-secondary"
                      style={{ padding: '8px 16px' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="btn-primary"
                      style={{ width: 'auto', padding: '8px 20px', borderRadius: '8px' }}
                    >
                      <Save size={15} /> {saving ? 'Saving...' : 'Save New Template Version'}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  {/* Test Dispatch Bar */}
                  <div
                    style={{
                      padding: '10px 20px',
                      background: '#f1f5f9',
                      borderBottom: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '10px',
                    }}
                  >
                    <input
                      type="email"
                      value={testRecipientEmail}
                      onChange={(e) => setTestRecipientEmail(e.target.value)}
                      placeholder="Optional test recipient email (e.g. safety@airline.aero)"
                      style={{ ...inputStyle, width: '320px' }}
                    />
                    <button
                      type="button"
                      onClick={handleDispatchTestEmail}
                      disabled={dispatchingTest}
                      className="btn-primary"
                      style={{ width: 'auto', padding: '7px 16px', borderRadius: '6px', fontSize: '12px' }}
                    >
                      <Send size={13} /> {dispatchingTest ? 'Dispatching...' : 'Send Test via Resend Outbox'}
                    </button>
                  </div>

                  {previewMode === 'source' ? (
                    <textarea
                      readOnly
                      value={selectedTemplate.renderedNormal.html}
                      style={{
                        width: '100%',
                        height: '560px',
                        background: '#0f172a',
                        color: '#e2e8f0',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        padding: '16px',
                        border: 'none',
                        resize: 'none',
                      }}
                    />
                  ) : (
                    <iframe
                      title={`preview-${selectedTemplate.templateKey}-${previewMode}`}
                      sandbox="allow-same-origin"
                      srcDoc={
                        previewMode === 'confidential'
                          ? selectedTemplate.renderedConfidential.html
                          : selectedTemplate.renderedNormal.html
                      }
                      style={{ width: '100%', height: '560px', border: 'none', background: '#f1f5f9' }}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* =====================================================================
          TAB 4: TRANSACTIONAL OUTBOX & DELIVERY LOG
         ===================================================================== */}
      {subTab === 'outbox_audit' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh field="templateKey" currentField={outboxSortField} currentDirection={outboxSortDir} onSort={toggleOutboxSort}>
                  Template Key
                </SortableTh>
                <SortableTh field="recipientEmail" currentField={outboxSortField} currentDirection={outboxSortDir} onSort={toggleOutboxSort}>
                  Recipient
                </SortableTh>
                <SortableTh field="subject" currentField={outboxSortField} currentDirection={outboxSortDir} onSort={toggleOutboxSort}>
                  Subject
                </SortableTh>
                <SortableTh field="classification" currentField={outboxSortField} currentDirection={outboxSortDir} onSort={toggleOutboxSort}>
                  Classification &amp; PII Shield
                </SortableTh>
                <SortableTh field="deliveryStatus" currentField={outboxSortField} currentDirection={outboxSortDir} onSort={toggleOutboxSort}>
                  Delivery Status &amp; Error Diagnostics
                </SortableTh>
                <SortableTh field="createdAt" currentField={outboxSortField} currentDirection={outboxSortDir} onSort={toggleOutboxSort}>
                  Timestamp
                </SortableTh>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedOutbox.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                    No email dispatches recorded yet.
                  </td>
                </tr>
              ) : (
                sortedOutbox.map((item) => (
                  <tr key={item.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb', fontSize: '12px' }}>
                      {item.templateKey}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: '#1e293b' }}>{item.recipientEmail}</div>
                      {item.recipientRole && (
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{item.recipientRole}</div>
                      )}
                    </td>
                    <td style={{ maxWidth: '280px', color: '#334155' }}>{item.subject}</td>
                    <td>
                      <span className={`badge-pill ${item.piiRedacted ? 'badge-warning' : 'badge-neutral'}`}>
                        {item.confidentialityLevel} {item.piiRedacted ? '• PII Redacted' : ''}
                      </span>
                    </td>
                    <td>
                      <div>
                        <span
                          className={`badge-pill ${
                            item.status.startsWith('SENT')
                              ? 'badge-success'
                              : item.status === 'DELIVERED_SIMULATED'
                              ? 'badge-info'
                              : item.status.startsWith('SKIPPED')
                              ? 'badge-warning'
                              : 'badge-danger'
                          }`}
                        >
                          {item.status === 'DELIVERED_SIMULATED' ? 'SIMULATED OUTBOX' : item.status}
                        </span>
                        {item.errorMessage && (
                          <div
                            style={{
                              fontSize: '11px',
                              color: item.status === 'FAILED' || item.status === 'DEAD_LETTER' ? '#dc2626' : '#64748b',
                              marginTop: '4px',
                              maxWidth: '300px',
                              lineHeight: 1.35,
                              wordBreak: 'break-word',
                            }}
                          >
                            {item.errorMessage}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {item.createdAt ? new Date(item.createdAt).toISOString().replace('T', ' ').slice(0, 19) : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {(item.status === 'FAILED' || item.status === 'DEAD_LETTER' || item.status === 'DELIVERED_SIMULATED') && (
                        <button
                          type="button"
                          onClick={() => handleRetryOutbox(item.id)}
                          disabled={retryingId === item.id}
                          className="btn-secondary"
                          style={{ padding: '5px 10px', fontSize: '11px', whiteSpace: 'nowrap' }}
                        >
                          {retryingId === item.id ? 'Retrying...' : 'Retry Delivery'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
